import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import type { ModelAssignmentStrategy } from "../../../api/src/firms/firms.service";

export type { ModelAssignmentStrategy };

export type ModelSource = "account" | "household" | "residual" | "none";

interface ModelSourceBadgeProps {
  source: ModelSource;
  strategy?: ModelAssignmentStrategy;
  showTooltip?: boolean;
}

const sourceConfig: Record<
  ModelSource,
  { label: string; variant: string; description: string }
> = {
  account: {
    label: "Account",
    variant: "primary",
    description: "Model explicitly assigned to this account",
  },
  household: {
    label: "Household",
    variant: "secondary",
    description: "Inherited from household model",
  },
  residual: {
    label: "Calculated",
    variant: "info",
    description:
      "Target calculated via Completion Strategy to achieve household allocation",
  },
  none: {
    label: "No Model",
    variant: "warning",
    description: "No model assigned at any level",
  },
};

/**
 * Badge component to indicate where an account's model comes from.
 * Shows different colors based on source type and optional tooltip with description.
 */
const ModelSourceBadge = ({
  source,
  strategy,
  showTooltip = true,
}: ModelSourceBadgeProps) => {
  const config = sourceConfig[source];

  const badge = (
    <Badge
      bg={config.variant}
      pill
      className="ms-2"
      style={{ fontSize: "0.7rem", fontWeight: 500 }}
    >
      {config.label}
    </Badge>
  );

  if (!showTooltip) {
    return badge;
  }

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`model-source-tooltip-${source}`}>
          {config.description}
          {strategy && source === "residual" && (
            <>
              <br />
              <small>Strategy: {strategy}</small>
            </>
          )}
        </Tooltip>
      }
    >
      {badge}
    </OverlayTrigger>
  );
};

export default ModelSourceBadge;

/**
 * Determine the model source for an account based on its model assignments.
 */
export function getModelSource(
  accountModelId: number | null | undefined,
  householdModelId: number | null | undefined,
  strategy: ModelAssignmentStrategy = "fallback",
): ModelSource {
  // Account.assignedModelId is an explicit null for an unassigned account
  // (APP-1535), which `!== undefined` read as "assigned". Both parameters now
  // treat null and undefined alike, the way the household branch below does.
  if (accountModelId != null) {
    return "account";
  }
  if (householdModelId != null) {
    return "household";
  }
  if (strategy === "completion") {
    return "residual";
  }
  return "none";
}

interface StrategyBadgeProps {
  strategy: ModelAssignmentStrategy;
  showLabel?: boolean;
}

const strategyConfig: Record<
  ModelAssignmentStrategy,
  { label: string; variant: string; description: string }
> = {
  fallback: {
    label: "Fallback",
    variant: "secondary",
    description:
      "Accounts inherit model from household. Accounts without models have no target allocation.",
  },
  preference: {
    label: "Preference",
    variant: "info",
    description:
      "Rules-based model assignment based on account attributes and preferences.",
  },
  completion: {
    label: "Completion",
    variant: "success",
    description:
      "Accounts without models receive calculated targets to achieve household allocation.",
  },
  soft_completion: {
    label: "Soft Completion",
    variant: "primary",
    description:
      "Account models are soft preferences constrained to fit within the household's per-asset-class budget.",
  },
};

/**
 * Badge component to display the model assignment strategy.
 */
export const StrategyBadge = ({
  strategy,
  showLabel = true,
}: StrategyBadgeProps) => {
  const config = strategyConfig[strategy];

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`strategy-tooltip-${strategy}`}>
          {config.description}
        </Tooltip>
      }
    >
      <Badge
        bg={config.variant}
        pill
        style={{ fontSize: "0.75rem", fontWeight: 500 }}
      >
        {showLabel && config.label}
      </Badge>
    </OverlayTrigger>
  );
};
